/**
 * RootErrorBoundary — catches uncaught render errors anywhere in the tree.
 *
 * Without this, a thrown error inside a page component takes down the
 * whole app and the user sees a blank white screen. With it, the user
 * sees a calm error card with "reload" and "go back" CTAs, and the
 * error gets logged to the console (and to whatever telemetry we add
 * later).
 *
 * Class component because React's error boundary API is only available
 * on classes — there is no hook equivalent as of React 19. The reset
 * key allows callers to reset the boundary when the route changes
 * (otherwise the broken state persists across navigation).
 *
 * Uses i18n — but if the error happens *inside* the i18n provider
 * itself we fall back to English literals to avoid an infinite loop.
 */
import { Component } from 'react'
import { withTranslation } from 'react-i18next'
import {
  HiOutlineExclamationTriangle,
  HiOutlineArrowPath,
  HiOutlineArrowLeft,
} from 'react-icons/hi2'

class RootErrorBoundaryInner extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, info: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // Keep the structured info on state so the details disclosure can render it.
    this.setState({ info })
    // eslint-disable-next-line no-console
    console.error('[RootErrorBoundary]', error, info)
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null })
  }

  componentDidUpdate(prevProps) {
    // Reset when the route key changes so a navigation away from a
    // broken page gives the user a working tree again.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset()
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const { t } = this.props
    const tt = (key, fallback) => {
      try { return t(key) || fallback } catch { return fallback }
    }

    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full">
          <div className="bg-surface border border-border rounded-2xl shadow-token-md p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-danger-soft text-danger flex-shrink-0">
                <HiOutlineExclamationTriangle className="w-6 h-6" />
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-semibold text-ink-primary leading-tight">
                  {tt('errorBoundary.title', 'Something went wrong')}
                </h1>
                <p className="text-sm text-ink-secondary mt-2 leading-relaxed">
                  {tt('errorBoundary.body', "An unexpected error stopped this part of the app from rendering. The error has been logged. You can try reloading the page or going back.")}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium text-ink-secondary hover:bg-muted hover:text-ink-primary transition-colors rtl:[&_svg]:rotate-180"
              >
                <HiOutlineArrowLeft className="w-4 h-4" />
                {tt('errorBoundary.back', 'Go back')}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-hover transition-colors"
              >
                <HiOutlineArrowPath className="w-4 h-4" />
                {tt('errorBoundary.reload', 'Reload page')}
              </button>
            </div>

            {/* Dev-only technical details disclosure */}
            {import.meta.env?.DEV && this.state.error && (
              <details className="mt-6 border-t border-border-subtle pt-4">
                <summary className="text-xs font-medium text-ink-muted cursor-pointer hover:text-ink-secondary">
                  {tt('errorBoundary.technicalDetails', 'Technical details')}
                </summary>
                <pre className="mt-2 p-3 bg-muted rounded-lg text-[11px] text-ink-secondary overflow-auto max-h-64 whitespace-pre-wrap">
                  {String(this.state.error?.stack || this.state.error)}
                  {this.state.info?.componentStack && '\n\n' + this.state.info.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    )
  }
}

const RootErrorBoundary = withTranslation()(RootErrorBoundaryInner)
export default RootErrorBoundary
